import React, { useEffect, useRef } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { User } from "@/types";
import AvatarPlaceholder from "@/components/ui/avatar-placeholder";
import { Users } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";

interface GolferTabsProps {
  golfers: User[];
  selectedGolferId: string | "all";
  onGolferChange: (golferId: string | "all") => void;
  // Add this for backward compatibility
  activeGolfer?: string; 
}

const GolferTabs = ({ golfers, selectedGolferId, onGolferChange, activeGolfer }: GolferTabsProps) => {
  // Use activeGolfer as fallback if provided
  const activeId = selectedGolferId || activeGolfer || "all";
  const tabsListRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  
  // Ensure the active tab is visible in the scroll view
  useEffect(() => {
    if (tabsListRef.current && activeId !== "all") {
      const activeTab = tabsListRef.current.querySelector(`[data-value="${activeId}"]`);
      if (activeTab) {
        // Get the tab's position
        const tabRect = activeTab.getBoundingClientRect();
        const containerRect = tabsListRef.current.getBoundingClientRect();
        
        // Check if tab is not fully visible
        if (tabRect.left < containerRect.left || tabRect.right > containerRect.right) {
          // Calculate scroll position to center the tab
          const scrollPosition = activeTab.scrollLeft + tabRect.left - containerRect.left - 
            (containerRect.width / 2) + (tabRect.width / 2);
          
          tabsListRef.current.scrollTo({ left: scrollPosition, behavior: 'smooth' });
        }
      }
    }
  }, [activeId]);

  // For mobile, render a dropdown selector instead of tabs
  if (isMobile) {
    return (
      <div className="mb-4">
        <Select value={activeId} onValueChange={onGolferChange}>
          <SelectTrigger className="w-full bg-white">
            <SelectValue placeholder="Select golfer">
              {activeId === "all" ? (
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4" />
                  <span>All Golfers</span>
                </div>
              ) : (
                <>
                  {golfers.find(g => g.id === activeId)?.name || "Select golfer"}
                </>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              <div className="flex items-center space-x-2">
                <Users className="h-4 w-4" />
                <span>All Golfers</span>
              </div>
            </SelectItem>
            
            {golfers.map((golfer) => (
              <SelectItem key={golfer.id} value={golfer.id}>
                <div className="flex items-center space-x-2">
                  <AvatarPlaceholder 
                    user={golfer}
                    size="sm"
                    className=""
                  />
                  <span>{golfer.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }
  
  // For desktop, keep the original tabs UI
  return (
    <Tabs 
      value={activeId} 
      onValueChange={onGolferChange} 
      className="mb-4"
    >
      <TabsList 
        ref={tabsListRef}
        className="flex w-full overflow-x-auto mb-4 p-1 bg-muted rounded-md relative"
        style={{ scrollbarWidth: 'none' }}
      >
        <TabsTrigger
          key="all"
          value="all"
          className="flex-shrink-0 data-[state=active]:bg-forest data-[state=active]:text-white px-4 py-2 rounded-md transition-colors"
          data-value="all"
        >
          <div className="flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span>All</span>
          </div>
        </TabsTrigger>
        
        {golfers.map((golfer) => (
          <TabsTrigger
            key={golfer.id}
            value={golfer.id}
            className="flex-shrink-0 data-[state=active]:bg-forest data-[state=active]:text-white px-4 py-2 rounded-md transition-colors"
            data-value={golfer.id}
          >
            <div className="flex items-center space-x-2">
              <AvatarPlaceholder 
                user={golfer}
                size="sm"
                className=""
              />
              <span className="truncate max-w-[80px]">{golfer.name}</span>
            </div>
          </TabsTrigger>
        ))}
      </TabsList>

      <TabsContent key="all" value="all" className="space-y-4">
        {/* This is intentionally empty as the content is managed by the parent component */}
      </TabsContent>
      
      {golfers.map((golfer) => (
        <TabsContent key={golfer.id} value={golfer.id} className="space-y-4">
          {/* This is intentionally empty as the content is managed by the parent component */}
        </TabsContent>
      ))}
    </Tabs>
  );
};

export default GolferTabs;
